import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  Eye,
  EyeOff,
  Plus,
  RefreshCw,
  Server,
} from 'lucide-react';
import StepShell from './StepShell';
import StepTitleWithInfo from './StepTitleWithInfo';
import type { FlowCtx, WifiNetwork } from './types';

interface Props {
  ctx: FlowCtx;
}

type View = 'form' | 'picker';

const signalBars = (level: number): 1 | 2 | 3 | 4 => {
  // Wi-Fi RSSI in dBm — typical thresholds.
  if (level >= -55) return 4;
  if (level >= -65) return 3;
  if (level >= -75) return 2;
  return 1;
};

const wifiBand = (frequency: number): '2.4GHz' | '5GHz' | null => {
  if (frequency >= 2400 && frequency <= 2500) return '2.4GHz';
  if (frequency >= 4900 && frequency <= 5900) return '5GHz';
  return null;
};

const SignalIcon: React.FC<{ bars: 1 | 2 | 3 | 4 }> = ({ bars }) => (
  <svg
    width="20"
    height="14"
    viewBox="0 0 20 14"
    fill="none"
    aria-hidden="true"
  >
    <rect x="0" y="10" width="3" height="4" rx="1" fill={bars >= 1 ? '#1F2937' : '#D1D5DB'} />
    <rect x="5" y="7" width="3" height="7" rx="1" fill={bars >= 2 ? '#1F2937' : '#D1D5DB'} />
    <rect x="10" y="4" width="3" height="10" rx="1" fill={bars >= 3 ? '#1F2937' : '#D1D5DB'} />
    <rect x="15" y="0" width="3" height="14" rx="1" fill={bars >= 4 ? '#1F2937' : '#D1D5DB'} />
  </svg>
);

const Step3WifiConfig: React.FC<Props> = ({ ctx }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [view, setView] = useState<View>('form');
  const [selectedBSSID, setSelectedBSSID] = useState<string | null>(null);

  const clearError = (key: keyof typeof ctx.validationErrors) => {
    if (ctx.validationErrors[key]) {
      ctx.setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  /* Get System status */
  useEffect(() => {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ action: 'GET_SYSTEM_STATUS' }),
    );
  }, [])

  const openPicker = () => {
    if (ctx.isIOSDevice) {
      // iOS cannot enumerate nearby networks; open system WiFi settings.
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({ action: 'OPEN_WIFI_SETTINGS_FOR_IOS_WIFI_CHANGE' })
      );
      return;
    }
    setView('picker');
    // Refresh the list when entering the picker so it isn't stale.
    ctx.rescanWifi();
  };

  const selectNetwork = (network: WifiNetwork) => {
    ctx.setWifiName(network.SSID);
    ctx.setWifiPassword('');
    setSelectedBSSID(network.BSSID);
    clearError('wifiname');
    setView('form');
  };

  if (view === 'picker') {
    return (
      <StepShell
        stepNumber={3}
        onBack={ctx.openExitModal}
        secondary={{ label: 'Cancel', onClick: () => setView('form') }}
      >
        <StepTitleWithInfo>
          <h2 className="dcf-step-title">Step 3 of 4: Select Wi-Fi Network</h2>
        </StepTitleWithInfo>
        <div className="dcf-step-bar"></div>

        {ctx.scanningWifi ? (
          <div className="dcf-wifi-scanning">
            <span>Scanning for networks…</span>
          </div>
        ) : (
        <ul className="dcf-wifi-list">
          {[...ctx.wifiNetworks]
            .sort((a, b) => {
              const aSelected = selectedBSSID ? a.BSSID === selectedBSSID : a.SSID === ctx.wifiname;
              const bSelected = selectedBSSID ? b.BSSID === selectedBSSID : b.SSID === ctx.wifiname;
              if (aSelected && !bSelected) return -1;
              if (bSelected && !aSelected) return 1;
              return 0;
            })
            .map((network) => {
              const isSelected = selectedBSSID
                ? network.BSSID === selectedBSSID
                : network.SSID === ctx.wifiname;
              // const secured = isSecured(network.capabilities);
              return (
                <li
                  key={`${network.BSSID}-${network.SSID}`}
                  className={`dcf-wifi-row ${isSelected ? 'is-selected' : ''}`}
                  onClick={() => selectNetwork(network)}
                >
                  <span
                    className={`dcf-wifi-row__radio ${isSelected ? 'is-checked' : ''}`}
                    aria-hidden="true"
                  />
                  <div className="dcf-wifi-row__main">
                    <div className="dcf-wifi-row__ssid">
                      {network.SSID}
                      {(() => {
                        const band = wifiBand(network.frequency);
                        if (!band) return null;
                        return (
                          <span
                            className={`dcf-wifi-row__band dcf-wifi-row__band--${band === '2.4GHz' ? 'low' : 'high'}`}
                          >
                            {band}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="dcf-wifi-row__meta">
                      {isSelected ? 'Saved Network' : 'Public'}
                    </div>
                  </div>
                  {network.encrypted && (
                    <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.66667 17.5C1.20833 17.5 0.815972 17.3368 0.489583 17.0104C0.163194 16.684 0 16.2917 0 15.8333V7.5C0 7.04167 0.163194 6.64931 0.489583 6.32292C0.815972 5.99653 1.20833 5.83333 1.66667 5.83333H2.5V4.16667C2.5 3.01389 2.90625 2.03125 3.71875 1.21875C4.53125 0.40625 5.51389 0 6.66667 0C7.81944 0 8.80208 0.40625 9.61458 1.21875C10.4271 2.03125 10.8333 3.01389 10.8333 4.16667V5.83333H11.6667C12.125 5.83333 12.5174 5.99653 12.8438 6.32292C13.1701 6.64931 13.3333 7.04167 13.3333 7.5V15.8333C13.3333 16.2917 13.1701 16.684 12.8438 17.0104C12.5174 17.3368 12.125 17.5 11.6667 17.5H1.66667ZM6.66667 13.3333C7.125 13.3333 7.51736 13.1701 7.84375 12.8438C8.17014 12.5174 8.33333 12.125 8.33333 11.6667C8.33333 11.2083 8.17014 10.816 7.84375 10.4896C7.51736 10.1632 7.125 10 6.66667 10C6.20833 10 5.81597 10.1632 5.48958 10.4896C5.16319 10.816 5 11.2083 5 11.6667C5 12.125 5.16319 12.5174 5.48958 12.8438C5.81597 13.1701 6.20833 13.3333 6.66667 13.3333ZM4.16667 5.83333H9.16667V4.16667C9.16667 3.47222 8.92361 2.88194 8.4375 2.39583C7.95139 1.90972 7.36111 1.66667 6.66667 1.66667C5.97222 1.66667 5.38194 1.90972 4.89583 2.39583C4.40972 2.88194 4.16667 3.47222 4.16667 4.16667V5.83333Z" fill="#434655" />
                    </svg>
                  )}
                  <SignalIcon bars={signalBars(network.level)} />
                </li>
              );
            })}
        </ul>
        )}

        <button
          type="button"
          className="dcf-wifi-rescan"
          onClick={() => ctx.rescanWifi()}
          disabled={ctx.scanningWifi}
        >
          <RefreshCw
            size={16}
            className={ctx.scanningWifi ? 'dcf-wifi-rescan__spin' : undefined}
          />
          {ctx.scanningWifi ? 'Scanning…' : 'Rescan'}
        </button>

        <button
          type="button"
          className="dcf-wifi-add-manual"
          onClick={() => setView('form')}
        >
          <Plus size={16} />
          Can&apos;t find the network? Add Manually
        </button>
      </StepShell>
    );
  }

  return (
    <StepShell
      stepNumber={3}
      onBack={ctx.openExitModal}
      secondary={{ label: 'Back', onClick: () => ctx.goTo('set') }}
      primary={{
        label: 'Next',
        onClick: () => ctx.verify(),
        loading: ctx.loading,
        loadingLabel: 'Verifying…',
      }}
    >
      <StepTitleWithInfo>
        <h2 className="dcf-step-title">Step 3 of 4: Enter Wi-Fi and server details</h2>
      </StepTitleWithInfo>
     
      <div className="dcf-step-bar"></div>

      <form className="dcf-form" onSubmit={(e) => e.preventDefault()}>
        <div className="dcf-field">
          <label className="dcf-field__label">Wi-Fi Network Name (SSID) <span className="text-red-500">*</span></label>
          <div className="dcf-field__input-wrap">
            <input
              type="text"
              value={ctx.wifiname}
              onChange={(e) => {
                ctx.setWifiName(e.target.value);
                clearError('wifiname');
              }}
              placeholder="Enter network name"
              className={`dcf-field__input dcf-field__input--with-action ${ctx.validationErrors.wifiname && 'input-error-border'}`}
              required
            />
            <button
              type="button"
              onClick={openPicker}
              className="dcf-field__action-btn"
            >
              {ctx.wifiname ? 'Change' : 'Select'}
            </button>
          </div>
          <span className="dcf-field__hint">
            Device only supports 2.4&nbsp;GHz Wi-Fi
          </span>
          {ctx.validationErrors.wifiname && (
            <span className="dcf-field__error">
              <AlertCircle className="h-4 w-4" />
              {ctx.validationErrors.wifiname}
            </span>
          )}
        </div>

        <div className="dcf-field">
          <label className="dcf-field__label">Wi-Fi Password <span className="text-red-500">*</span></label>
          <div className="dcf-field__input-wrap">
            <input
              type={showPassword ? 'text' : 'password'}
              value={ctx.wifipassword}
              onChange={(e) => {
                ctx.setWifiPassword(e.target.value);
                clearError('password');
              }}
              placeholder="Enter your Wi-Fi password"
              className={`dcf-field__input dcf-field__input--with-icon ${ctx.validationErrors.password && 'input-error-border'}`}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="dcf-field__icon-btn"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>
          {ctx.validationErrors.password && (
            <span className="dcf-field__error">
              <AlertCircle className="h-4 w-4" />
              {ctx.validationErrors.password}
            </span>
          )}
        </div>

        <div className="dcf-field">
          <label className="dcf-field__label">Server URL</label>
          <div className="dcf-field__input-wrap">
            <input
              type="text"
              value={ctx.serverUrl}
              onChange={(e) => ctx.setServerUrl(e.target.value)}
              placeholder="Enter the server URL"
              className="dcf-field__input dcf-field__input--with-icon"
              required
            />
            <span className="dcf-field__icon-btn" aria-hidden="true">
              <Server className="h-4 w-4" />
            </span>
          </div>
          <span className="dcf-field__hint dcf-field__hint--muted">
            Make sure this URL matches your regional hub
          </span>
        </div>

        {ctx.showWarning && (
          <div className="dcf-banner dcf-banner--warning">
            <AlertCircle size={20} color="#EEC94D" className="flex-shrink-0" />
            <div>
              <p style={{ margin: 0, fontWeight: 600 }}>
                Unable to detect your Wi-Fi details?
              </p>
              <p style={{ margin: 0 }}>
                Please enter your Wi-Fi name and password manually.
              </p>
            </div>
          </div>
        )}
      </form>
    </StepShell>
  );
};

export default Step3WifiConfig;
